import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, Link, NavLink, useNavigate, useSearchParams, Navigate, Outlet } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/common/StatusBadge";
import { toast } from "sonner";
import {
  Home, FileText, Upload as UploadIcon, Wrench, LogOut, Bell, Send,
  Mail, MapPin, Coins, AlertTriangle, CheckCircle2,
} from "lucide-react";

const API = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

/* ----- Auth context ----- */
const TenantAuthCtx = React.createContext(null);

function TenantAuthProvider({ children }) {
  const [me, setMe] = useState(undefined);
  const load = useCallback(async () => {
    try { const { data } = await API.get("/tenant-portal/me"); setMe(data); }
    catch (e) { setMe(null); }
  }, []);
  useEffect(() => {
    // Skip the initial /me probe on public routes to avoid a noisy 401 in the console.
    const path = window.location.pathname;
    if (path.startsWith("/tenant/login") || path.startsWith("/tenant/magic")) {
      setMe(null);
    } else {
      load();
    }
  }, [load]);
  const logout = async () => {
    try { await API.post("/tenant-portal/auth/logout"); }
    catch (err) { console.warn("Tenant logout call failed (proceeding client-side):", err); }
    setMe(null);
  };
  return <TenantAuthCtx.Provider value={{ me, setMe, load, logout }}>{children}</TenantAuthCtx.Provider>;
}

function useTenantAuth() { return React.useContext(TenantAuthCtx); }

/* ----- Root component ----- */
export default function TenantPortalApp() {
  return (
    <TenantAuthProvider>
      <Routes>
        <Route path="login" element={<LoginPage />} />
        <Route path="magic" element={<MagicConsumePage />} />
        <Route element={<Guard><PortalLayout /></Guard>}>
          <Route index element={<Dashboard />} />
          <Route path="documents" element={<Documents />} />
          <Route path="upload" element={<UploadPage />} />
          <Route path="interventions" element={<Interventions />} />
          <Route path="messages" element={<Messages />} />
        </Route>
      </Routes>
    </TenantAuthProvider>
  );
}

function Guard({ children }) {
  const { me } = useTenantAuth();
  if (me === undefined) return <div className="p-10 text-muted-foreground">Chargement…</div>;
  if (!me) return <Navigate to="/tenant/login" replace />;
  return children;
}

/* ----- Login ----- */
function LoginPage() {
  const [tab, setTab] = useState("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const { load } = useTenantAuth();
  const navigate = useNavigate();

  const requestMagic = async () => {
    if (!email) return;
    try {
      await API.post("/tenant-portal/auth/request-magic-link", { email });
    } catch { /* on n'expose rien : réponse identique en cas d'erreur */ }
    // Message unique quel que soit le résultat (anti-énumération)
    toast.success("Si cette adresse est reconnue, un lien de connexion vient d'être envoyé.");
    setMagicSent(true);
  };

  const doLogin = async () => {
    try {
      await API.post("/tenant-portal/auth/login", { email, password });
      await load(); navigate("/tenant"); toast.success("Bienvenue");
    } catch (e) { toast.error("Identifiants invalides"); }
  };

  return (
    <div className="min-h-screen bg-secondary/40 flex items-center justify-center p-6">
      <div className="max-w-md w-full card-elevated p-8" data-testid="tenant-login">
        <div className="text-center mb-6">
          <div className="font-heading text-3xl">Espace locataire</div>
          <div className="text-sm text-muted-foreground mt-1">Fonbex — accès sécurisé</div>
        </div>
        <div className="flex gap-1 mb-6 p-1 bg-secondary rounded-full">
          <button onClick={() => { setTab("magic"); setMagicSent(false); }}
                  className={`flex-1 rounded-full py-2 text-sm ${tab === "magic" ? "bg-white shadow" : ""}`}>
            Lien magique
          </button>
          <button onClick={() => setTab("password")}
                  className={`flex-1 rounded-full py-2 text-sm ${tab === "password" ? "bg-white shadow" : ""}`}>
            Mot de passe
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <Label>E-mail</Label>
            <Input type="email" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   data-testid="tenant-email"
                   placeholder="vous@example.fr" />
          </div>
          {tab === "password" && (
            <div>
              <Label>Mot de passe</Label>
              <Input type="password" value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     data-testid="tenant-password" />
            </div>
          )}
          {tab === "magic" ? (
            <Button onClick={requestMagic} className="w-full btn-pill"
                    disabled={!email || magicSent}
                    data-testid="tenant-magic-btn">
              <Mail className="mr-2 h-4 w-4" />
              {magicSent ? "Lien envoyé" : "Recevoir un lien de connexion"}
            </Button>
          ) : (
            <Button onClick={doLogin} className="w-full btn-pill"
                    data-testid="tenant-login-btn">
              Se connecter
            </Button>
          )}
        </div>
        {tab === "magic" && magicSent && (
          <div className="mt-4 rounded-xl bg-secondary/40 border border-border p-3 text-xs text-muted-foreground text-center"
               data-testid="tenant-magic-sent">
            Si cette adresse est reconnue, un lien de connexion vient
            d&apos;être envoyé. Le lien expire dans 30 minutes et ne peut
            être utilisé qu&apos;une seule fois.
          </div>
        )}
      </div>
    </div>
  );
}

function MagicConsumePage() {
  const [params] = useSearchParams();
  const { load } = useTenantAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  useEffect(() => {
    (async () => {
      try {
        await API.post("/tenant-portal/auth/consume-magic-link", { token: params.get("token") });
        await load();
        setState("ok"); setTimeout(() => navigate("/tenant"), 600);
      } catch { setState("error"); }
    })();
  }, [params, load, navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center">
      {state === "loading" && <div>Connexion en cours…</div>}
      {state === "ok" && <div className="text-emerald-700">Connecté. Redirection…</div>}
      {state === "error" && (
        <div className="text-red-700">Lien invalide ou expiré. <Link to="/tenant/login" className="underline">Retour</Link></div>
      )}
    </div>
  );
}

/* ----- Layout ----- */
function PortalLayout() {
  const { me, logout } = useTenantAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/tenant/login"); };
  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b">
        <div className="max-w-3xl mx-auto flex items-center justify-between p-4">
          <div>
            <div className="font-heading text-lg">Espace locataire</div>
            <div className="text-xs text-muted-foreground">{me?.user?.email}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="tenant-logout"><LogOut className="mr-2 h-4 w-4" />Quitter</Button>
        </div>
      </header>

      {/* Mobile bottom nav / Desktop top nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-10 md:static md:max-w-3xl md:mx-auto md:mt-4 bg-white md:bg-transparent border-t md:border-0">
        <div className="grid grid-cols-5 md:flex md:gap-2">
          <TabLink to="/tenant" icon={Home} label="Accueil" testId="tenant-nav-home" end />
          <TabLink to="/tenant/documents" icon={FileText} label="Documents" testId="tenant-nav-documents" />
          <TabLink to="/tenant/upload" icon={UploadIcon} label="Déposer" testId="tenant-nav-upload" />
          <TabLink to="/tenant/interventions" icon={Wrench} label="Demandes" testId="tenant-nav-interventions" />
          <TabLink to="/tenant/messages" icon={Send} label="Messages" testId="tenant-nav-messages" />
        </div>
      </nav>

      <main className="max-w-3xl mx-auto p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}

function TabLink({ to, icon: Icon, label, testId, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) => `flex flex-col md:flex-row items-center gap-1 md:gap-2 py-3 md:py-2 md:px-4 md:rounded-full text-xs md:text-sm ${isActive ? "text-primary md:bg-primary/10" : "text-muted-foreground"}`}>
      <Icon className="h-5 w-5" />{label}
    </NavLink>
  );
}

/* ----- Dashboard ----- */
function Dashboard() {
  const [dash, setDash] = useState(null);
  useEffect(() => { API.get("/tenant-portal/dashboard").then(({ data }) => setDash(data)); }, []);
  if (!dash) return <div className="text-muted-foreground">Chargement…</div>;
  if (!dash.has_lease) return <div className="card-elevated p-6">Aucun bail actif rattaché à votre compte.</div>;
  return (
    <div className="space-y-4" data-testid="tenant-dashboard">
      <div className="card-elevated p-6">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Votre logement</div>
        <div className="font-heading text-2xl mt-1">{dash.lot?.reference}</div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1"><MapPin className="h-3 w-3" />{dash.building?.address}</div>
        <div className="mt-3 flex items-center gap-4 text-sm">
          <div><span className="text-muted-foreground">Loyer TTC : </span><strong>{dash.lease.rent_ttc?.toLocaleString("fr-FR")} €</strong></div>
          <StatusBadge tone="success">{dash.lease.status}</StatusBadge>
        </div>
      </div>

      {dash.alerts?.length > 0 && (
        <div className="space-y-2">
          {dash.alerts.map((a, i) => (
            <div key={i} className="card-elevated p-3 flex items-center gap-2 text-sm" data-testid={`tenant-alert-${a.kind}`}>
              <AlertTriangle className={`h-4 w-4 ${a.severity === "warning" ? "text-amber-600" : "text-primary"}`} />
              {a.label}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-elevated p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Prochain loyer</div>
          {dash.next_installment ? (
            <>
              <div className="font-serif text-2xl mt-1">{dash.next_installment.amount_expected.toLocaleString("fr-FR")} €</div>
              <div className="text-xs text-muted-foreground">Échéance {dash.next_installment.due_date}</div>
              {dash.next_installment.remaining > 0 && (
                <div className="text-xs mt-1">Restant : <strong>{dash.next_installment.remaining.toLocaleString("fr-FR")} €</strong></div>
              )}
            </>
          ) : <div className="text-sm text-muted-foreground mt-2">Aucune échéance à venir.</div>}
        </div>
        <div className="card-elevated p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dernier paiement</div>
          {dash.last_payment ? (
            <>
              <div className="font-serif text-2xl mt-1"><CheckCircle2 className="inline h-5 w-5 text-emerald-600" /> {dash.last_payment.amount.toLocaleString("fr-FR")} €</div>
              <div className="text-xs text-muted-foreground">{dash.last_payment.period_label} · {dash.last_payment.paid_at}</div>
            </>
          ) : <div className="text-sm text-muted-foreground mt-2">Aucun paiement enregistré.</div>}
        </div>
      </div>

      {dash.latest_document && (
        <div className="card-elevated p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dernier document</div>
          <div className="mt-2 flex items-center justify-between">
            <div>
              <div className="font-medium">Quittance {dash.latest_document.period_label}</div>
              <div className="text-xs text-muted-foreground">Émise le {new Date(dash.latest_document.issued_at).toLocaleDateString("fr-FR")}</div>
            </div>
            <Link to="/tenant/documents"><Button size="sm" variant="outline">Voir</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----- Documents ----- */
function Documents() {
  const [items, setItems] = useState([]);
  useEffect(() => { API.get("/tenant-portal/documents").then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <div className="space-y-3" data-testid="tenant-documents">
      {items.length === 0 && <div className="text-muted-foreground text-center py-12">Aucun document disponible.</div>}
      {items.map((d) => (
        <div key={d.id} className="card-elevated p-4 flex items-center justify-between" data-testid={`doc-${d.id}`}>
          <div>
            <div className="font-medium">{d.label}</div>
            <div className="text-xs text-muted-foreground">{d.kind} · {d.reference || ""} · {new Date(d.issued_at || 0).toLocaleDateString("fr-FR")}</div>
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api${d.download_url}`} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline">Télécharger</Button>
          </a>
        </div>
      ))}
    </div>
  );
}

/* ----- Upload ----- */
function UploadPage() {
  const { me } = useTenantAuth();
  const [kind, setKind] = useState("assurance");
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [uploads, setUploads] = useState([]);
  const load = () => API.get("/tenant-portal/documents").then(({ data }) => setUploads((data.items || []).filter(d => d.kind.startsWith("upload_"))));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!file || !me?.leases?.[0]) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await API.post("/tenant-portal/upload", {
          kind, filename: file.name, content_b64: reader.result, mime: file.type,
          notes, lease_id: me.leases[0].id,
        });
        toast.success("Document déposé"); setFile(null); setNotes(""); load();
      } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-4" data-testid="tenant-upload">
      <div className="card-elevated p-5">
        <div className="font-heading mb-4">Déposer un document</div>
        <div className="space-y-3">
          <div><Label>Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="upload-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assurance">Attestation d&apos;assurance habitation</SelectItem>
                <SelectItem value="chaudiere">Entretien chaudière</SelectItem>
                <SelectItem value="diagnostic">Diagnostic</SelectItem>
                <SelectItem value="attestation">Autre attestation</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Fichier (PDF ou image)</Label>
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file" />
          </div>
          <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <Button onClick={submit} disabled={!file} className="w-full btn-pill" data-testid="upload-submit"><UploadIcon className="mr-2 h-4 w-4" />Envoyer</Button>
        </div>
      </div>
      <div className="space-y-2">
        {uploads.map((u) => (
          <div key={u.id} className="card-elevated p-3 flex items-center justify-between">
            <div className="text-sm">{u.label} — <StatusBadge tone={u.status === "approved" ? "success" : u.status === "rejected" ? "danger" : "warning"}>{u.status}</StatusBadge></div>
            <span className="text-xs text-muted-foreground">{new Date(u.issued_at).toLocaleDateString("fr-FR")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----- Interventions ----- */
function Interventions() {
  const { me } = useTenantAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "plomberie", priority: "medium", description: "" });
  const load = () => API.get("/tenant-portal/interventions").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.description || !me?.leases?.[0]) return;
    try {
      await API.post("/tenant-portal/interventions", { ...form, lease_id: me.leases[0].id });
      toast.success("Demande envoyée"); setForm({ category: "plomberie", priority: "medium", description: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const STATUS_TONE = { new: "warning", acknowledged: "neutral", in_progress: "neutral", done: "success", closed: "neutral" };
  const STATUS_LABEL = { new: "Nouveau", acknowledged: "Pris en charge", in_progress: "En cours", done: "Terminé", closed: "Clos" };
  return (
    <div className="space-y-4" data-testid="tenant-interventions">
      <div className="card-elevated p-5">
        <div className="font-heading mb-3">Nouvelle demande</div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="inter-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{["fuite", "chauffage", "electricite", "plomberie", "serrurerie", "electromenager", "autre"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Priorité</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="inter-desc" /></div>
          <Button onClick={submit} disabled={!form.description} className="w-full btn-pill" data-testid="inter-submit">Envoyer la demande</Button>
        </div>
      </div>
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.id} className="card-elevated p-4" data-testid={`inter-row-${i.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-medium capitalize">{i.category}</div>
              <StatusBadge tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</StatusBadge>
            </div>
            <div className="text-sm text-muted-foreground mt-1">{i.description}</div>
            <div className="text-xs text-muted-foreground mt-1">Priorité : {i.priority} · {new Date(i.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----- Messages ----- */
function Messages() {
  const { me } = useTenantAuth();
  const [threads, setThreads] = useState([]);
  const [body, setBody] = useState("");
  const load = () => API.get("/tenant-portal/messages").then(({ data }) => setThreads(data.threads || []));
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!body || !me?.leases?.[0]) return;
    try {
      await API.post("/tenant-portal/messages", { body, lease_id: me.leases[0].id });
      toast.success("Envoyé"); setBody(""); load();
    } catch (e) { toast.error("Erreur"); }
  };
  return (
    <div className="space-y-4" data-testid="tenant-messages">
      {threads.length === 0 && <div className="text-center text-muted-foreground py-8">Aucun message. Envoyez-en un ci-dessous.</div>}
      {threads.map((t) => (
        <div key={t.thread_id} className="card-elevated p-4 space-y-2">
          {t.messages.map((m) => (
            <div key={m.id} className={`rounded-xl p-3 max-w-[80%] ${m.sender_type === "tenant" ? "bg-primary/10 ml-auto" : "bg-secondary"}`}>
              <div className="text-sm">{m.body}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{new Date(m.sent_at).toLocaleString("fr-FR")}</div>
            </div>
          ))}
        </div>
      ))}
      <div className="card-elevated p-3 flex gap-2">
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Votre message…" className="flex-1" data-testid="msg-body" />
        <Button onClick={send} disabled={!body} data-testid="msg-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
